/**
 * The single HTTP entry point for the SPA.
 *
 * The bearer token lives in module scope (set by the auth context) and is never persisted:
 * localStorage/sessionStorage are unavailable in this environment, so a reload means a fresh
 * sign-in. A 401 from any call clears auth and bounces the user back to the sign-in screen.
 */

import type { ApiErrorBody } from './types';

export const API_BASE = '/api';

/** Error thrown by every failed API call; carries the server's ApiError body when there is one. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly detail: string | null;

  constructor(status: number, code: string, message: string, detail: string | null = null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.detail = detail;
  }

  /** True when the failure is a lost/expired session rather than a bad request. */
  get isUnauthorized(): boolean {
    return this.status === 401;
  }
}

// ---------------------------------------------------------------------------
// Token plumbing (owned by AuthProvider)
// ---------------------------------------------------------------------------

let accessToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function getAccessToken(): string | null {
  return accessToken;
}

/** Registered by AuthProvider so a 401 anywhere tears the session down exactly once. */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

/** The error code TenantVerificationFilter puts on a licence-gate 403. */
export const TENANT_NOT_VERIFIED_CODE = 'TenantNotVerified';

let tenantNotVerifiedHandler: (() => void) | null = null;

/**
 * Registered by OnboardingProvider. When any API call is refused by the server's licence gate, the
 * cached onboarding state is stale — the licence lapsed, or the account was suspended, after the
 * state was loaded — so the provider re-fetches and the UI falls back to the locked screens. This
 * is what keeps a long-lived browser session honest without polling.
 */
export function setTenantNotVerifiedHandler(handler: (() => void) | null): void {
  tenantNotVerifiedHandler = handler;
}

function notifyIfTenantNotVerified(error: ApiError): void {
  if (error.status === 403 && error.code === TENANT_NOT_VERIFIED_CODE) {
    tenantNotVerifiedHandler?.();
  }
}

function authHeaders(extra?: HeadersInit): Headers {
  const headers = new Headers(extra);
  if (!headers.has('Accept')) headers.set('Accept', 'application/json');
  if (accessToken) headers.set('Authorization', `Bearer ${accessToken}`);
  return headers;
}

async function toApiError(response: Response): Promise<ApiError> {
  let code = `http_${response.status}`;
  let message = response.statusText || 'The request failed.';
  let detail: string | null = null;

  const contentType = response.headers.get('content-type') ?? '';
  try {
    if (contentType.includes('application/json')) {
      const body = (await response.json()) as Partial<ApiErrorBody> | null;
      if (body && typeof body === 'object') {
        // Some 422 refusals carry the server's own domain response instead of an ApiError body —
        // PostDocumentResponse (message/errorCode at the top level) or VendorIntakeApprovalResponse
        // (the same two fields nested under `posting`). Without this, the person only ever saw the
        // generic "The request failed.", and code-driven UI (like a company-mismatch override) had
        // nothing to key off.
        const nested = (body as Record<string, unknown>).posting;
        const source: Record<string, unknown> =
          nested && typeof nested === 'object' ? (nested as Record<string, unknown>) : body;

        if (typeof body.code === 'string' && body.code.length > 0) {
          code = body.code;
        } else if (typeof source.errorCode === 'string' && source.errorCode.length > 0) {
          code = source.errorCode;
        }

        if (typeof body.message === 'string' && body.message.length > 0) {
          message = body.message;
        } else if (typeof source.message === 'string' && source.message.length > 0) {
          message = source.message;
        }

        if (typeof body.detail === 'string' && body.detail.length > 0) detail = body.detail;
      }
    } else {
      const text = (await response.text()).trim();
      if (text.length > 0) detail = text.slice(0, 2000);
    }
  } catch {
    // Body was unreadable or not JSON after all; the status-derived message stands.
  }

  if (response.status === 401) {
    message = 'Your session has expired. Please sign in again.';
  }

  return new ApiError(response.status, code, message, detail);
}

export type QueryValue = string | number | boolean | null | undefined;

/** Builds a query string, dropping null/undefined/empty values so filters stay tidy. */
export function buildQuery(params: Record<string, QueryValue>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined) continue;
    if (typeof value === 'string' && value.trim() === '') continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs.length > 0 ? `?${qs}` : '';
}

export interface ApiFetchOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Plain object (JSON-encoded automatically) or FormData/Blob for raw bodies. */
  body?: unknown;
  signal?: AbortSignal;
  headers?: HeadersInit;
}

/**
 * Typed fetch wrapper. Attaches the bearer token, JSON-encodes object bodies, converts failures
 * into `ApiError`, and returns `undefined as T` for 204/empty responses.
 */
export async function apiFetch<T>(path: string, options: ApiFetchOptions = {}): Promise<T> {
  const { method = 'GET', body, signal, headers } = options;
  const requestHeaders = authHeaders(headers);

  let payload: BodyInit | undefined;
  if (body instanceof FormData || body instanceof Blob || typeof body === 'string') {
    payload = body;
  } else if (body !== undefined && body !== null) {
    requestHeaders.set('Content-Type', 'application/json');
    payload = JSON.stringify(body);
  }

  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: requestHeaders,
      body: payload,
      signal,
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(
      0,
      'network_error',
      'Could not reach the gateway API.',
      cause instanceof Error ? cause.message : null,
    );
  }

  if (response.status === 401) {
    const error = await toApiError(response);
    unauthorizedHandler?.();
    throw error;
  }

  if (!response.ok) {
    const error = await toApiError(response);
    notifyIfTenantNotVerified(error);
    throw error;
  }

  if (response.status === 204 || response.headers.get('content-length') === '0') {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) {
    const text = await response.text();
    return (text.length > 0 ? (text as unknown as T) : (undefined as T));
  }

  const text = await response.text();
  if (text.length === 0) return undefined as T;
  return JSON.parse(text) as T;
}

export function apiGet<T>(path: string, signal?: AbortSignal): Promise<T> {
  return apiFetch<T>(path, { method: 'GET', signal });
}

export function apiPost<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return apiFetch<T>(path, { method: 'POST', body, signal });
}

export function apiPut<T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  return apiFetch<T>(path, { method: 'PUT', body, signal });
}

export function apiDelete<T>(path: string, signal?: AbortSignal): Promise<T> {
  return apiFetch<T>(path, { method: 'DELETE', signal });
}

// ---------------------------------------------------------------------------
// Binary fetch (document preview)
// ---------------------------------------------------------------------------

export interface BlobResult {
  /** Object URL for the fetched bytes. Callers MUST revoke it when done. */
  url: string;
  contentType: string;
}

/**
 * Fetches a protected binary endpoint as an object URL. The preview pane cannot put an
 * Authorization header on an <iframe>/<img> src, so the bytes are pulled here first.
 */
export async function fetchBlobUrl(path: string, signal?: AbortSignal): Promise<BlobResult> {
  let response: Response;
  try {
    response = await fetch(`${API_BASE}${path}`, { headers: authHeaders(), signal });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw new ApiError(
      0,
      'network_error',
      'Could not reach the gateway API.',
      cause instanceof Error ? cause.message : null,
    );
  }

  if (response.status === 401) {
    const error = await toApiError(response);
    unauthorizedHandler?.();
    throw error;
  }
  if (!response.ok) {
    const error = await toApiError(response);
    notifyIfTenantNotVerified(error);
    throw error;
  }

  const blob = await response.blob();
  const contentType = response.headers.get('content-type') ?? blob.type ?? 'application/octet-stream';
  return { url: URL.createObjectURL(blob), contentType };
}

// ---------------------------------------------------------------------------
// Multipart upload with progress
// ---------------------------------------------------------------------------

export interface UploadHandle<T> {
  promise: Promise<T>;
  abort: () => void;
}

/**
 * POSTs multipart/form-data with per-file progress. `fetch` cannot report upload progress, so this
 * uses XMLHttpRequest directly.
 */
export function apiUpload<T>(
  path: string,
  form: FormData,
  onProgress?: (percent: number) => void,
): UploadHandle<T> {
  const xhr = new XMLHttpRequest();

  const promise = new Promise<T>((resolve, reject) => {
    xhr.open('POST', `${API_BASE}${path}`, true);
    xhr.responseType = 'text';
    xhr.setRequestHeader('Accept', 'application/json');
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);

    xhr.upload.onprogress = (event: ProgressEvent) => {
      if (!onProgress) return;
      // Cap at 99% while the server is still working; the caller shows 100% on success.
      const percent = event.lengthComputable
        ? Math.min(99, Math.round((event.loaded / event.total) * 100))
        : 0;
      onProgress(percent);
    };

    xhr.onerror = () =>
      reject(new ApiError(0, 'network_error', 'Could not reach the gateway API.'));

    xhr.onabort = () => reject(new ApiError(0, 'upload_aborted', 'Upload cancelled.'));

    xhr.onload = () => {
      const status = xhr.status;
      const text = xhr.responseText ?? '';

      if (status === 401) {
        unauthorizedHandler?.();
        reject(new ApiError(401, 'unauthorized', 'Your session has expired. Please sign in again.'));
        return;
      }

      if (status >= 200 && status < 300) {
        try {
          resolve(text.length > 0 ? (JSON.parse(text) as T) : (undefined as T));
        } catch {
          reject(new ApiError(status, 'bad_response', 'The gateway returned an unreadable response.'));
        }
        return;
      }

      let code = `http_${status}`;
      let message = xhr.statusText || 'The upload failed.';
      let detail: string | null = null;
      try {
        const body = JSON.parse(text) as Partial<ApiErrorBody>;
        if (typeof body.code === 'string' && body.code) code = body.code;
        if (typeof body.message === 'string' && body.message) message = body.message;
        if (typeof body.detail === 'string' && body.detail) detail = body.detail;
      } catch {
        if (text.trim().length > 0) detail = text.slice(0, 2000);
      }

      // Uploads participate in the stale-licence refresh like every other API path: a gate 403 on
      // a drag-and-drop means the cached onboarding state is out of date.
      const error = new ApiError(status, code, message, detail);
      notifyIfTenantNotVerified(error);
      reject(error);
    };

    xhr.send(form);
  });

  return { promise, abort: () => xhr.abort() };
}

/** Normalises anything thrown by an API call into a user-facing message. */
export function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  if (error instanceof Error) return error.message;
  return 'Something went wrong.';
}
